import React, { useState, useEffect, useRef, cloneElement } from 'react';
import './Mainpage.css';

// Custom hook for IntersectionObserver for performance-optimized animations
function useIntersectionObserver(options) {
  const reportConversion = (url) => {
    const callback = () => {
      if (url) window.location = url;
    };

    if (typeof window.gtag !== "undefined") {
      window.gtag('event', 'conversion', {
        send_to: 'AW-17689802022/O3ecCNz3tbYbEKbqk_NB',
        value: 1.0,
        currency: 'INR',
        event_callback: callback
      });
    }
    return false;
  };


  const ref = useRef(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          element.classList.add('is-visible');
          if (options?.triggerOnce) {
            observer.unobserve(element);
          }
        }
      },
      options
    );

    observer.observe(element);
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options]);
  return ref;
}

function reportConversion(url) {
  const callback = () => {
    if (url) window.location = url;
  };

  if (typeof window.gtag !== "undefined") {
    window.gtag('event', 'conversion', {
      send_to: 'AW-17689802022/O3ecCNz3tbYbEKbqk_NB',
      value: 1.0,
      currency: 'INR',
      event_callback: callback
    });
    return true;
  } else {
    console.warn("Google Ads gtag not found");
    return false;
  }
}

// SOLUTION: Create a wrapper component that correctly uses the hook.
function AnimationWrapper({ children }) {
  const ref = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });

  if (!React.isValidElement(children)) {
    return children;
  }

  const className = `${children.props.className || ''} fade-in-on-scroll`.trim();
  return cloneElement(children, { ref, className });
}

function Mainpage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [countersStarted, setCountersStarted] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const statsRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !countersStarted) {
          setCountersStarted(true);
          animateCounters();
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => {
      if (statsRef.current) observer.unobserve(statsRef.current);
    };
  }, [countersStarted]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (selectedProduct && !isFullscreen) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedProduct.images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [selectedProduct, isFullscreen]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedProduct) return;
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        else closeProductModal();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedProduct, isFullscreen]);

  const animateCounters = () => {
    document.querySelectorAll('.counter').forEach(counter => {
      const target = parseInt(counter.getAttribute('data-target'));
      const duration = 2000;
      const increment = target / (duration / 16);
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          counter.textContent = target.toLocaleString();
          clearInterval(timer);
        } else {
          counter.textContent = Math.floor(current).toLocaleString();
        }
      }, 16);
    });
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setIsFullscreen(false);
    document.body.style.overflow = 'hidden';
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);
    setIsFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (!selectedProduct) return;
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedProduct.images.length);
  };

  const prevImage = () => {
    if (!selectedProduct) return;
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? selectedProduct.images.length - 1 : prevIndex - 1));
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Our Products", href: "#products" },
    { name: "Services", href: "#services" },
    { name: "About Us", href: "#about" },
    { name: "Gallery", href: "#gallery" },
  ];

  const products = [
    { name: "Roller Blinds", description: "Stylish and functional roller blinds for precise light control.", fullDescription: "Our roller blinds combine contemporary style with practical functionality. Featuring smooth operation and precise light control, these blinds are perfect for modern homes and offices.", images: ["/Service2.1.jpeg", "/Service2.2.jpeg", "/Service2.3.jpeg"], features: ["Sleek minimalist design", "Easy operation", "Precise light control"], details: ["Manual or motorized", "Wide range of colors", "Moisture-resistant fabrics", "Child-safe options", "Custom sizing", "Quick installation"], benefits: ["Space-saving design", "Low maintenance", "Durable construction", "Excellent value"] },
    { name: "Sheer Window Treatments", description: "Filter light and provide privacy with elegant sheer treatments.", fullDescription: "Transform your space with our premium sheer window treatments. These elegant coverings provide the perfect balance between privacy and natural light, creating a soft, luminous atmosphere in any room.", images: ["/Service1.1.jpeg", "/Service1.2.jpeg", "/Service1.3.jpeg"], features: ["Various materials & colors", "Modern versatile design", "Pairs with drapes"], details: ["Available in custom sizes", "UV protection", "Easy to clean", "Energy efficient", "Professional installation included", "5-year warranty"], benefits: ["Softens sunlight", "Enhances privacy", "Creates elegant atmosphere", "Reduces glare"] },
    { name: "Blockout Curtains", description: "Ensure full darkness, privacy, and noise reduction for restful sleep.", fullDescription: "Experience ultimate darkness and privacy with our premium Blockout curtains. Specially designed with multiple layers of light-blocking fabric, they create the perfect environment for rest.", images: ["/Service5.1.png"], features: ["Complete darkness", "Thermal insulation", "UV & noise blocking"], details: ["100% light-blocking", "Reduces energy costs", "Noise reduction", "Wide selection of styles", "Machine washable", "Custom sizing"], benefits: ["Improves sleep quality", "Protects furniture", "Ideal for home theaters", "Reduces outside noise"] },
    { name: "Plantation Shutters", description: "Elegant and energy-efficient shutters that become part of your decor.", fullDescription: "Elevate your home with our premium plantation shutters. These timeless window treatments add instant value and sophistication to any property.", images: ["/Service4.1.jpeg", "/Service4.2.jpeg", "/Service4.3.jpeg"], features: ["Elegant focal point", "Energy efficient", "Easy maintenance"], details: ["Timber, PVC, aluminum", "Custom finishes", "Adjustable louvers", "Increases property value", "Excellent insulation", "Lifetime warranty"], benefits: ["Adds architectural interest", "Reduces energy bills", "Easy to clean", "Timeless design"] },
    { name: "Motorised Curtains", description: "Automated curtain systems with remote control and smart home integration.", fullDescription: "Step into the future with our motorized curtain systems. Control your window treatments with the touch of a button, voice command, or automated schedule.", images: ["/Service6.1.png"], features: ["Smart home ready", "Remote control", "Quiet operation"], details: ["Alexa, Google Home compatible", "Programmable timers", "Whisper-quiet motors", "Battery backup", "Smartphone app control", "Professional setup"], benefits: ["Ultimate convenience", "Perfect for high windows", "Enhances home security", "Increases property value"] },
    { name: "Roller Motorised Blinds", description: "Automated roller blinds for modern convenience and style.", fullDescription: "Experience the perfect blend of technology and design with our Roller Motorised Blinds. Operated via remote control or smart home integration, they deliver effortless light management and privacy.", images: ["/Service3.1.jpeg"], features: ["Fully motorised", "Smooth and silent", "Smart home compatible"], details: ["Blockout, light-filtering, sheer", "Remote, app, voice control", "Custom sizing", "Energy-efficient", "Child-safe design", "Simple installation"], benefits: ["Enhances comfort", "Ideal for high windows", "Improves energy efficiency", "Adds luxury"] },
  ];

  const services = [
    { icon: "📏", title: "Free Measure & Quote", description: "Our experts visit your home for accurate measurements and a detailed, no-cost quote." },
    { icon: "🔧", title: "Custom Manufacturing", description: "Every product is made-to-order using premium materials and advanced techniques." },
    { icon: "✓", title: "Quality Guaranteed", description: "We deliver premium-quality window treatments backed by our satisfaction guarantee." },
    { icon: "🛡️", title: "Professional Installation", description: "Expert installation by certified technicians for a perfect fit and finish." }
  ];

  const galleryImages = [
    { url: "/Gallery 1.jpeg", alt: "Modern living room with elegant sheer curtains" },
    { url: "/Service1.2.jpeg", alt: "Dinning space with white sheer curtains providing soft light" },
    { url: "/Service2.1.jpeg", alt: "Kitchen space with modern roller blinds" },
    { url: "/Gallery 2.png", alt: "Dinning Area featuring Modern sheer curtains" },
    { url: "/Service3.1.jpeg", alt: "Motorised roller blinds in a smart home living room" },
    { url: "/Service5.1.png", alt: "Luxury bedroom with dark grey Blockout curtains" }
  ];

  const testimonials = [
    { name: "Thushara Ranasinghe", location: "Melbourne, Australia", rating: 5, text: "Very happy with the service and quality of curtains was very good and classy. Highly recommend for Curton service." },
    { name: "Gurpreet Singh Sekhon", location: "Melbourne, Australia", rating: 5, text: "Great service and quality! MD Blinds & Curtains did an amazing job. The team was friendly, professional, and the blinds look fantastic. Highly recommend for anyone looking to upgrade their windows!" },
    { name: "Manpreet Aulakh", location: "Melbourne, Australia", rating: 5, text: "I’m really happy with how everything turned out and would definitely recommend MD blinds to anyone looking for quality curtains and reliable service.The installation was quick and hassle-free. Everything was done on time and with great care." },
    { name: "Rupali Katoch", location: "Melbourne, Australia", rating: 5, text: "I will recommend MD Blinds &Curtains to everyone! We got our Blinds done for our new Home. Not just the quality of work but the way he treats his customers is the most appreciating thing. Very quick Service and very professional. Worth getting your Blinds and curtains done from MD Blinds & Curtains. Once again Thanku very much  for giving our Home New look. We are very grateful to you. “The Best In Melbourne”." },
    { name: "Mission Baruwal", location: "Melbourne, Australia", rating: 5, text: "We recently purchased sheer curtains for our home and couldn’t be happier with the service we received. Maninder communicated regularly in the lead up to the installation and was very helpful in giving advice in selecting the right curtains for our home. Our curtains look great, were very competitively priced and come with years of warranty which wasn’t offered by others. I highly recommend MD Blinds and Curtains." },
    { name: "Sandeep Singh Tiwana", location: "Melbourne, Australia", rating: 5, text: "MD Blinds and Curtains provide top-quality products with a perfect blend of style and functionality. Their designs are modern, elegant, and built to last. The service is professional, and the results truly elevate any space. Highly recommended." }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
  
    try {
      const response = await fetch("https://formspree.io/f/xrbyrjae", {
        method: "POST",
        body: formData,
        headers: { 'Accept': 'application/json' }
      });
  
      if (response.ok) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        form.reset();
  
        // ✅ Trigger conversion only after successful submission
        reportConversion();
      } else {
        alert("Oops! There was a problem submitting your form.");
      }
    } catch (error) {
      alert("Oops! There was a network error. Please try again.");
    }
  };
  

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // =========================================================
  // SEO Optimization: Structured Data (JSON-LD)
  // !!! IMPORTANT: REPLACE "https://www.your-website.com.au" with your actual domain !!!
  const YOUR_WEBSITE_URL = "https://mdblindscurtains.com/";
  // =========================================================

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "MDBLINDSCURTAINS",
    "description": "Premium custom blinds, curtains, and plantation shutters in Melbourne. We offer free measure and quote, professional installation, and a wide range of styles including motorised and Blockout options.",
    "telephone": "+61451082509",
    "email": "mdblindscurtains@gmail.com",
    "address": { "@type": "PostalAddress", "addressLocality": "Melbourne", "addressRegion": "VIC", "addressCountry": "AU" },
    "url": YOUR_WEBSITE_URL,
    "logo": `${YOUR_WEBSITE_URL}/logo.png`,
    "image": `${YOUR_WEBSITE_URL}/landing-page-1.png`,
    "openingHours": "Mo-Fr 08:00-18:00, Sa 08:00-12:00",
    "priceRange": "$$",
    "makesOffer": products.map(p => ({
      "@type": "Offer",
      "itemOffered": { "@type": "Product", "name": p.name, "description": p.description, "image": `${YOUR_WEBSITE_URL}${p.images[0]}` }
    })),
    "review": testimonials.map(t => ({
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": t.rating, "bestRating": "5" },
      "author": { "@type": "Person", "name": t.name },
      "reviewBody": t.text
    }))
  };

  return (
    <div className="app">
      <title>MDBLINDSCURTAINS</title>
      <meta name="description" content="Discover premium, custom-made blinds, curtains, and plantation shutters in Melbourne with MDBLINDSCURTAINS. Free measure & quote, professional installation, and stylish solutions for your home." />
      <link rel="canonical" href={YOUR_WEBSITE_URL} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>

      <nav className={`navigation ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <a href="#home" onClick={scrollToTop}>
                <img src="/logo.png" alt="MDBLINDS & Curtains Logo" className="logo-image" />
                <span className="logo-text">MDBLINDSCURTAINS</span>
              </a>
            </div>
            <div className="desktop-nav">
              {navLinks.map((link, index) => (
                <a key={link.name} href={link.href} className="nav-link" style={{ animationDelay: `${index * 0.1}s` }}>{link.name}</a>
              ))}
              <a href="#contact">
                <button className="btn btn-primary btn-glow">
                  <span className="btn-icon">✆</span>
                  <span>Get Free Quote</span>
                </button>
              </a>
            </div>
            <button className={`mobile-menu-btn ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div className={`mobile-nav ${isMenuOpen ? 'active' : ''}`}>
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>{link.name}</a>
            ))}
            <a href="#contact" onClick={() => setIsMenuOpen(false)}>
              <button className="btn btn-primary w-full btn-glow">
                <span className="btn-icon">✆</span>
                <span>Get Free Quote</span>
              </button>
            </a>
          </div>
        </div>
      </nav>

      <section id="home" className="hero">
        <div className="hero-background">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
        </div>
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="badge fade-in">
                <span className="star shimmer">★</span>
                <span className="badge-text">Premium Quality • 200+ Happy Customers</span>
                <div className="badge-glow"></div>
              </div>
              <h1 className="hero-title slide-up">Elevate Your Windows <span className="text-gradient">with Style</span></h1>
              <p className="hero-description slide-up-delay">Your Partner in Perfect Shading in Melbourne - Delivering exceptional service and unparalleled expertise for your home or workspace.</p>
              <div className="hero-buttons slide-up-delay-2">
                <a href="#contact">
                  <button className="btn btn-primary btn-lg btn-glow">
                    <span>Schedule Consultation</span>
                    <span className="btn-arrow">→</span>
                  </button>
                </a>
                <a href="#products">
                  <button className="btn btn-outline btn-lg">
                    <span>Explore Collection</span>
                  </button>
                </a>
              </div>
              <div ref={statsRef} className="stats fade-in-up">
                <div className="stat">
                  <div className="stat-icon-circle"><div className="stat-icon">✓</div></div>
                  <p className="stat-number counter" data-target="270">0</p>
                  <p className="stat-label">Projects Completed</p>
                  <div className="stat-bar"></div>
                </div>
                <div className="stat">
                  <div className="stat-icon-circle"><div className="stat-icon">◆</div></div>
                  <p className="stat-number counter" data-target="3">0</p>
                  <p className="stat-label">Years Experience</p>
                  <div className="stat-bar"></div>
                </div>
                <div className="stat">
                  <div className="stat-icon-circle"><div className="stat-icon">★</div></div>
                  <p className="stat-number">100<span className="stat-percent">%</span></p>
                  <p className="stat-label">Satisfaction Rate</p>
                  <div className="stat-bar"></div>
                </div>
              </div>
            </div>
            <div className="hero-image-wrapper zoom-in">
              <div className="hero-image">
                <div className="image-overlay"></div>
                <img src="/landing page 1.png" alt="Elegant living room with sheer curtains and modern furniture in Melbourne" />
              </div>
              <div className="offer-badge pulse">
                <div className="offer-icon">✦</div>
                <p className="offer-label">Limited Time</p>
                <p className="offer-value">30% OFF</p>
                <p className="offer-subtext">FOR</p>
                <p className="offer-subtext">DOUBLE STORY</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="products" className="products">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Custom Window Treatments</h2>
            <div className="title-underline"></div>
            <p className="section-description">At MDBLINDSCURTAINS, we pride ourselves on delivering a wide range of custom-made blinds and curtains. From installation to maintenance, our team is dedicated to providing top-notch solutions tailored to your unique needs in Melbourne.</p>
          </div>
          <div className="products-grid">
            {products.map((product) => (
              <AnimationWrapper key={product.name}>
                <div className="product-card">
                  <div className="product-image">
                    <div className="product-overlay"></div>
                    <img src={product.images[0]} alt={product.name} loading="lazy" />
                    <div className="product-badge">Premium</div>
                    <div className="product-thumbnails">
                      {product.images.slice(1).map((img, idx) => (
                        <div key={idx} className="product-thumbnail">
                          <img src={img} alt={`${product.name} thumbnail ${idx + 2}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="product-content">
                    <h3 className="product-title">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                    <ul className="product-features">
                      {product.features.map((feature, idx) => (
                        <li key={idx}><span className="feature-icon">✓</span>{feature}</li>
                      ))}
                    </ul>
                    <button className="btn btn-secondary w-full btn-hover-lift" onClick={() => openProductModal(product)}>
                      <span>Discover More</span><span className="btn-arrow">→</span>
                    </button>
                  </div>
                </div>
              </AnimationWrapper>
            ))}
          </div>
        </div>
      </section>

      {selectedProduct && (
        <div className="modal-overlay" onClick={closeProductModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeProductModal} aria-label="Close modal">×</button>
            <div className="modal-grid">
              <div className="modal-slideshow">
                <div className="slideshow-container">
                  <img src={selectedProduct.images[currentImageIndex]} alt={`${selectedProduct.name} ${currentImageIndex + 1}`} className="slideshow-image" onClick={toggleFullscreen} style={{ cursor: 'pointer' }} />
                  <button className="slideshow-btn prev" onClick={prevImage} aria-label="Previous image">‹</button>
                  <button className="slideshow-btn next" onClick={nextImage} aria-label="Next image">›</button>
                  <button className="fullscreen-btn" onClick={toggleFullscreen} title="View Fullscreen" aria-label="View Fullscreen">⛶</button>
                  <div className="slideshow-dots">
                    {selectedProduct.images.map((_, index) => (
                      <span key={index} className={`dot ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)}></span>
                    ))}
                  </div>
                </div>
                <div className="slideshow-thumbnails">
                  {selectedProduct.images.map((img, index) => (
                    <img key={index} src={img} alt={`Thumbnail ${index + 1}`} className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`} onClick={() => setCurrentImageIndex(index)} />
                  ))}
                </div>
              </div>
              <div className="modal-details">
                <div className="modal-header"><h2>{selectedProduct.name}</h2></div>
                <p className="modal-description">{selectedProduct.fullDescription}</p>
                <div className="modal-section"><h3>Key Features</h3><ul className="modal-list">{selectedProduct.features.map((feature, idx) => (<li key={idx}><span className="list-icon">✓</span>{feature}</li>))}</ul></div>
                <div className="modal-section"><h3>Product Details</h3><ul className="modal-list">{selectedProduct.details.map((detail, idx) => (<li key={idx}><span className="list-icon">•</span>{detail}</li>))}</ul></div>
                <div className="modal-section"><h3>Benefits</h3><ul className="modal-list">{selectedProduct.benefits.map((benefit, idx) => (<li key={idx}><span className="list-icon">★</span>{benefit}</li>))}</ul></div>
                <div className="modal-actions"><a href="#contact" onClick={closeProductModal}><button className="btn btn-primary btn-lg w-full"><span>Get Free Quote</span><span className="btn-arrow">→</span></button></a></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isFullscreen && selectedProduct && (
        <div className="fullscreen-overlay" onClick={toggleFullscreen}>
          <button className="fullscreen-close" onClick={toggleFullscreen} aria-label="Close fullscreen">×</button>
          <img src={selectedProduct.images[currentImageIndex]} alt={`${selectedProduct.name} fullscreen`} className="fullscreen-image" onClick={(e) => e.stopPropagation()} />
          <button className="fullscreen-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }} aria-label="Previous fullscreen image">‹</button>
          <button className="fullscreen-next" onClick={(e) => { e.stopPropagation(); nextImage(); }} aria-label="Next fullscreen image">›</button>
          <div className="fullscreen-counter">{currentImageIndex + 1} / {selectedProduct.images.length}</div>
        </div>
      )}

      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose MDBLINDSCURTAINS</h2>
            <div className="title-underline"></div>
            <p className="section-description">From consultation to installation, we deliver excellence at every step</p>
          </div>
          <div className="services-grid">
            {services.map((service) => (
              <AnimationWrapper key={service.title}>
                <div className="service-card">
                  <div className="service-icon-wrapper">
                    <div className="service-icon">{service.icon}</div>
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <div className="service-line"></div>
                </div>
              </AnimationWrapper>
            ))}
          </div>
          <div className="offer-banner">
            <div className="offer-banner-content">
              <div className="offer-banner-icon">✦</div>
              <h3 className="offer-banner-title">Exclusive Limited-Time Savings</h3>
              <p className="offer-banner-text">Enjoy exceptional value with our tiered volume discounts</p>
              <div className="discount-tiers">
                <div className="discount-tier">
                  <div className="tier-icon">◆◆</div>
                  <p className="tier-label">Single Story</p>
                  <p className="tier-value">20% OFF</p>
                </div>
                <div className="discount-tier highlight">
                  <div className="tier-icon">◆◆◆</div>
                  <p className="tier-label">Double Story</p>
                  <p className="tier-value">30% OFF</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="gallery" className="gallery">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Work Gallery</h2>
            <div className="title-underline"></div>
            <p className="section-description">Inspiration for your next project - See how we transform spaces with style</p>
          </div>
          <div className="gallery-grid">
            {galleryImages.map((image) => (
              <AnimationWrapper key={image.url}>
                <div className="gallery-item">
                  <img src={image.url} alt={image.alt} loading="lazy" />
                  <div className="gallery-overlay">
                    <div className="gallery-content">
                      <p className="gallery-text">{image.alt}</p>
                    </div>
                  </div>
                </div>
              </AnimationWrapper>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Melbourne Clients Say</h2>
            <div className="title-underline"></div>
            <p className="section-description">Discover why discerning homeowners trust us with their most important spaces</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <AnimationWrapper key={testimonial.name}>
                <div className="testimonial-card">
                  <div className="quote-icon">"</div>
                  <div className="rating">
                    {[...Array(testimonial.rating)].map((_, i) => (<span key={i} className="star">★</span>))}
                  </div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-footer">
                    <div className="testimonial-author">
                      <div className="author-info">
                        <p className="author-name">{testimonial.name}</p>
                        <p className="author-location">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimationWrapper>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="services" style={{ background: 'linear-gradient(180deg, rgba(210, 229, 245, 0.3) 0%, rgba(210, 229, 245, 0.1) 100%)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">About MDBLINDSCURTAINS</h2>
            <div className="title-underline"></div>
            <p className="section-description">With over 3 years of experience, MDBLINDSCURTAINS has been transforming homes and offices across Melbourne. Our commitment to quality, customer service, and innovative solutions has made us the trusted choice for window treatments. We combine traditional craftsmanship with modern technology to deliver products that exceed expectations.</p>
          </div>
          <div className="hero-buttons" style={{ justifyContent: 'center', marginTop: '2rem' }}>
            <a href="#contact">
              <button className="btn btn-primary btn-lg btn-glow">
                <span>Learn More & Get a Quote</span>
                <span className="btn-arrow">→</span>
              </button>
            </a>
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get a Free Measure & Quote</h2>
            <div className="title-underline"></div>
            <p className="section-description">Get in touch with our experts today</p>
          </div>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="info-item">
                <div className="info-icon">📞</div>
                <div>
                  <h3>Booking Appointment</h3>
                  <p><a href="tel:+61451082509" style={{ color: 'inherit', textDecoration: 'none' }}>+61 451 082 509</a></p>
                  <p className="info-subtitle">Open: 8:00 AM - 6:00 PM</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">✉️</div>
                <div>
                  <h3>Email</h3>
                  <p><a href="mailto:mdblindscurtains@gmail.com" style={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all' }}>mdblindscurtains@gmail.com</a></p>
                  <p className="info-subtitle">We reply within 24 hours</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📍</div>
                <div>
                  <h3>Location</h3>
                  <p>Melbourne, Australia</p>
                  <p className="info-subtitle">Servicing the greater Melbourne area</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🕒</div>
                <div>
                  <h3>Business Hours</h3>
                  <p>Monday - Friday</p>
                  <p className="info-subtitle">8:00 AM - 6:00 PM</p>
                  <p>Saturday</p>
                  <p className="info-subtitle">8:00 AM - 12:00 PM</p>
                </div>
              </div>
              <div className="contact-promo">
                <h3>Special Offer!</h3>
                <p>Book your free measure & quote this week and receive a 10% discount on your order.</p>
                <a href="tel:+61451082509"><button className="btn btn-accent">Book Now</button></a>
              </div>
            </div>
            <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
  <div className="form-group">
    <label htmlFor="name">Full Name</label>
    <input id="name" type="text" name="name" placeholder="John Smith" required />
  </div>

  <div className="form-group">
    <label htmlFor="email">Email Address</label>
    <input id="email" type="email" name="email" placeholder="john@example.com" required />
  </div>

  <div className="form-group">
    <label htmlFor="phone">Phone Number</label>
    <input id="phone" type="tel" name="phone" placeholder="0400 000 000" required />
  </div>

  <div className="form-group">
    <label htmlFor="product">Interested In</label>
    <select id="product" name="product" required>
      <option value="">Select a product</option>
      <option value="Roller Blinds">Roller Blinds</option>
      <option value="Sheer Window Treatments">Sheer Window Treatments</option>
      <option value="Blockout Curtains">Blockout Curtains</option>
      <option value="Plantation Shutters">Plantation Shutters</option>
      <option value="Motorised Curtains">Motorised Curtains</option>
      <option value="BOTH : Roller And Sheer Window Blinds">
        BOTH : Roller And Sheer Window Blinds
      </option>
    </select>
  </div>

  {/* ✅ New Optional Field */}
  <div className="form-group">
    <label htmlFor="measurement">Measurement (optional)</label>
    <input
      id="measurement"
      type="text"
      name="measurement"
      placeholder="e.g., Width 120cm x Height 150cm"
    />
  </div>

  <div className="form-group">
    <label htmlFor="message">Message</label>
    <textarea
      id="message"
      name="message"
      placeholder="Tell us about your project..."
      rows="4"
      required
    ></textarea>
  </div>

  <button
    type="submit"
    className="btn btn-primary btn-lg w-full btn-glow"
    onClick={() => reportConversion()}
  >
    <span>Send Message</span>
    <span className="btn-arrow">→</span>
  </button>
</form>

            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-section">
              <h3 className="footer-logo">MDBLINDSCURTAINS</h3>
              <p className="footer-description">Your Partner in Perfect Shading - Premium window treatments for your home or office in Melbourne.</p>
              <div className="social-links">
                <a href="#" aria-label="Visit our Facebook page">📘</a>
                <a href="#" aria-label="See our work on Instagram">📷</a>
                <a href="#" aria-label="Follow us on Twitter">🐦</a>
                <a href="mailto:MDBLINDSCURTAINS@gmail.com" aria-label="Send us an Email">✉️</a>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">Products</h4>
              <ul>
                <li><a href="#products">Sheer Window Treatments</a></li>
                <li><a href="#products">Roller Blinds</a></li>
                <li><a href="#products">Plantation Shutters</a></li>
                <li><a href="#products">Blockout Curtains</a></li>
                <li><a href="#products">Motorised Curtains</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">Company</h4>
              <ul>
                <li><a href="#about">About Us</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#gallery">Gallery</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="#testimonials">Testimonials</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">Support</h4>
              <ul>
                <li><a href="#contact">FAQ</a></li>
                <li><a href="#contact">Warranty</a></li>
                <li><a href="#contact">Installation Guide</a></li>
                <li><a href="#contact">Care Instructions</a></li>
                <li><a href="#contact">Returns Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 MDBLINDSCURTAINS. All rights reserved.</p>
            <div className="footer-links">
              <a href="#contact">Privacy Policy</a>
              <a href="#contact">Terms of Service</a>
              <a href="#home" onClick={scrollToTop}>Back to Top</a>
            </div>
          </div>
        </div>
      </footer>

      {showToast && (
        <div className="toast">✓ Thank you! We'll contact you within 24 hours.</div>
      )}
    </div>
  );
}

export default Mainpage;